import h from 'hyperscript';
import cloneDeep from 'lodash-es/cloneDeep';
import horizontalLineIcon from '@disy/cadenza-icons/horizontal-line.svg';
import arrowIcon from '@disy/cadenza-icons/simple-arrow.svg';
import filledArrowIcon from '@disy/cadenza-icons/filled-arrow.svg';
import crossBarIcon from '@disy/cadenza-icons/crossbar.svg';

import { ColorPicker } from 'ui/color-picker/color-picker';
import { SliderNumberInput } from 'ui/slider-number-input/slider-number-input';
import { Select } from 'ui/select/select';

import {
  DEFAULT_COLOR_PALETTE,
  toHexColor,
  toRgbaColor,
  parseColor,
  getColorWithOpacity
} from 'cadenza/utils/colors/colors';
import { fonts } from 'cadenza/utils/fonts';
import { on, unByKey } from 'cadenza/utils/event-util';
import { SketchObjectType } from 'cadenza/map/classic-sketch-layer/sketch-object-type';
import { createSketchStrategy as sketchItemStrategy } from 'cadenza/map/classic-sketch-layer/strategy';
import {
  opacityAsFractionToTransparencyAsPercent,
  transparencyAsPercentToOpacityAsFraction
} from 'cadenza/map/layer-configuration-panel/layer-config-utils';
import { SketchFeatureProperty } from 'cadenza/map/classic-sketch-layer/sketch-feature-property';
import {
  modelStyleToOpenLayersStyle,
  getMainStyle, isRenderedAsPointImage, getOpacity
} from 'cadenza/map/classic-sketch-layer/sketch-style';
import { DELETE_KEY_CODE } from 'cadenza/map/classic-sketch-layer/sketch-sidebar/sketch-sidebar-content';
import { SKETCH_EVENTS } from 'cadenza/map/classic-sketch-layer/sketch-sidebar/sketch';
import { FillSignatureType, LineDecoration, StyleProperty } from 'cadenza/map/style/style-constants';
import { icon } from 'cadenza/utils/icon/icon';

import i18n from './sketch-feature-properties-editor.properties';
import './sketch-feature-properties-editor.css';

const NAME = 'd-sketch-feature-properties-editor';
const ANCHORS = [ 'SOUTH_EAST', 'SOUTH', 'SOUTH_WEST', 'EAST', 'CENTER', 'WEST', 'NORTH_EAST', 'NORTH', 'NORTH_WEST' ];

const PERCENT_UNIT_SYMBOL = '%';
const DEGREE_UNIT_SYMBOL = '°';

const FeatureProperty = {
  BORDER_COLOR: StyleProperty.BORDER_COLOR,
  LINE_WIDTH: StyleProperty.LINE_WIDTH,
  FOREGROUND_COLOR: StyleProperty.FOREGROUND_COLOR,
  OPACITY: 'opacity',
  SIGNATURE_SIZE: 'signatureSize',
  FONT_FAMILY: 'fontFamily',
  FONT_SIZE: 'fontSize',
  BOLD: 'bold',
  ITALIC: 'italic',
  BACKGROUND_VISIBLE: 'backgroundVisible',
  BACKGROUND_COLOR: StyleProperty.BACKGROUND_COLOR,
  ANCHOR: 'anchor',
  ROTATION: StyleProperty.ROTATION,
  LINE_START_DECORATION: StyleProperty.LINE_START_DECORATION,
  LINE_END_DECORATION: StyleProperty.LINE_END_DECORATION
};

FeatureProperty.values = Object.values(FeatureProperty);

const PROPERTIES_WITH_TRANSPARENCY = [ FeatureProperty.FOREGROUND_COLOR, FeatureProperty.BACKGROUND_COLOR ];

/**
 * Component for the feature properties editing.
 *
 * It shows the properties of the currently selected feature and allow to change them.
 *
 * If there are several properties selected (either individually or through a group), only the
 * properties that are relevant for all of them are shown.
 *
 * If all selected features also have the same value for a property, then the input is populated
 * using this value, otherwise the input is in an "undetermined" state.
 *
 * Changing a property value updates all the selected properties at once.
 */
export class SketchFeaturePropertiesEditor extends HTMLElement {

  _eventKeys = [];
  _selectedFeatures = [];

  _backgroundColorPicker;
  _transparencySlider;
  _rotationSlider;
  _strokeColorInput;
  _fillColorInput;

  /**
   * Constructor
   *
   * @param {Sketch} sketch - The model object of class Sketch
   */
  constructor (sketch) {
    super();

    this.classList.add(NAME, 'palette-container');

    this._createContent();
    this._initValidation();
    this._preventDeleteKeyBubbling();
    on(this, [ 'input', 'change' ], (e) => this._onPropertyChange(e), { delegate: '.property-input' });

    this._update();

    this._eventKeys.push(
      on(
        sketch,
        SKETCH_EVENTS.SELECTION_CHANGE,
        event => this._onSelectionChange(event.detail.features)
      ),
      on(
        sketch,
        SKETCH_EVENTS.UNDO_REDO_OPERATION,
        event => this._update()
      )
    );
  }

  _disconnectedCallback () {
    unByKey(this._eventKeys);
    this.eventKeys = [];
  }

  _onSelectionChange (selectedFeatures) {
    this._selectedFeatures = selectedFeatures;
    this._update();
  }

  _createContent () {
    this.append(
      h('.stretch-container.options-container',
        h('form.feature-properties-form', [

          h('.property-row.inline', [
            createFontFamilySelect(),
            createInput('.property-input.form-control.font-size', {
              name: FeatureProperty.FONT_SIZE,
              type: 'number',
              min: '6',
              max: '72',
              value: '16',
              required: true
            })
          ]),

          h('.property-row.font-style-row', [
            createInput('#bold-font-style.property-input.form-control', {
              type: 'checkbox',
              name: FeatureProperty.BOLD
            }),
            h('label', { htmlFor: 'bold-font-style' }, 'B'),
            createInput('#italic-font-style.property-input.form-control', {
              type: 'checkbox',
              name: FeatureProperty.ITALIC
            }),
            h('label', { htmlFor: 'italic-font-style' }, 'I')
          ]),

          h('.property-row', [
            h('label.property', i18n('style.strokeColor')),
            this._strokeColorInput = createColorPicker(FeatureProperty.BORDER_COLOR)
          ]),

          h('.property-row', [
            h('label.property', i18n('style.strokeWidth')),
            createInput('.property.property-input.form-control', {
              name: FeatureProperty.LINE_WIDTH,
              type: 'number',
              min: '1',
              max: '1000',
              value: '1',
              step: '0.1',
              required: true
            })
          ]),

          h('.property-row', [
            h('label.property', i18n('style.signatureSize')),
            createInput('.property.property-input.form-control', {
              name: FeatureProperty.SIGNATURE_SIZE,
              type: 'number',
              min: '1',
              max: '1000',
              value: '10',
              required: true
            })
          ]),

          h('.property-row', [
            h('label.property', i18n('style.fillColor')),
            this._fillColorInput = createColorPicker(FeatureProperty.FOREGROUND_COLOR)
          ]),

          h('.property-row', [
            h('label.property', i18n('style.backgroundColor')),
            h('.property.inline', [
              createInput('.property-input.background-visible', {
                name: FeatureProperty.BACKGROUND_VISIBLE,
                type: 'checkbox',
                title: i18n('style.backgroundVisible')
              }),
              this._backgroundColorPicker = createColorPicker(FeatureProperty.BACKGROUND_COLOR)
            ])
          ]),

          h('.property-row', [
            h('label.property', i18n('style.alignment')),
            createAnchorSelect()
          ]),

          h('.property-row.with-slider', [
            h('label.property', i18n('style.transparency')),
            this._transparencySlider = createSlider(FeatureProperty.OPACITY, {
              min: 0,
              max: 100,
              value: 0,
              unit: PERCENT_UNIT_SYMBOL
            })
          ]),

          h('.property-row.with-slider', [
            h('label.property', i18n('style.rotation')),
            this._rotationSlider = createSlider(FeatureProperty.ROTATION, {
              min: 0,
              max: 359,
              value: 0,
              unit: DEGREE_UNIT_SYMBOL
            })
          ]),

          h('.property-row', [
            h('label.property', i18n('style.startLineDecoration')),
            createLineDecorationSelect(FeatureProperty.LINE_START_DECORATION)
          ]),

          h('.property-row', [
            h('label.property', i18n('style.endLineDecoration')),
            createLineDecorationSelect(FeatureProperty.LINE_END_DECORATION)
          ])
        ])
      )
    );
  }

  _preventDeleteKeyBubbling () {
    // Prevent the delete key event in the inputs from bubbling up to the parent element
    // see CADENZA-11064
    on(this, 'keydown', (event) => {
      if (event.keyCode === DELETE_KEY_CODE) {
        event.stopPropagation();
      }
    });
  }

  _initValidation () {
    const form = this.querySelector('form.feature-properties-form');
    form.querySelectorAll('.property-input').forEach(propertyInput => {
      on(propertyInput, 'invalid', () => {
        propertyInput.closest('.property-row').classList.add('has-error');
      });
      on(propertyInput, 'input', () => {
        // empty number fields are allowed when several features are selected without a common value,
        // but as soon as a value is typed the field becomes required.
        if (propertyInput.type === 'number') {
          propertyInput.required = true;
        }
        const propertyRow = propertyInput.closest('.property-row');
        if (propertyRow) {
          propertyRow.classList.toggle('has-error', !form.checkValidity());
        }
      }, { delegate: '.property-input' });
    });

    on(form, 'reset', () => {
      form.querySelectorAll('.property-row').forEach(propertyRow => propertyRow.classList.remove('has-error'));
    });
  }

  /**
   * Update the UI, to be called every time the features selection has changed.
   *
   * @private
   */
  _update () {
    this._clearMessage();

    const visibleProperties = this._determineVisibleProperties();

    if (this._selectedFeatures.length === 0) {
      this._showMessage(i18n('sketch-sidebar.geometry-options-container.noSelectedFeature'));
    } else if (visibleProperties.length === 0) {
      this._showMessage(i18n('sketch-sidebar.geometry-options-container.noCommonProperty'));
    }

    FeatureProperty.values.forEach(property => {
      this._setPropertyVisible(property, visibleProperties.includes(property));
    });

    visibleProperties.forEach(property => {
      const value = this._determineCommonPropertyValue(property);
      this._setPropertyValue(property, value);
      if (property === FeatureProperty.BACKGROUND_VISIBLE) {
        this._backgroundColorPicker.disabled = !value;
      }
      if (property === FeatureProperty.FOREGROUND_COLOR) {
        const fillColorLabelElement = this._fillColorInput.parentElement.querySelector('.property');
        const areAllSelectedFeaturesLines = this._selectedFeatures.every(isLineFeature);
        // For line an polyline, the strokeColor is represented by the FOREGROUND_COLOR property which is normally labeled: "Fill color"
        fillColorLabelElement.textContent = i18n(areAllSelectedFeaturesLines ? 'style.strokeColor' : 'style.fillColor');
      }
    });
  }

  _determineVisibleProperties () {
    if (this._selectedFeatures.length === 0) {
      return [];
    }

    const propertyVisibleForAllFeatures = property => this._selectedFeatures.every(
      feature => getVisibleProperties(feature.get(SketchFeatureProperty.STYLE_ITEM)).includes(property)
    );

    return FeatureProperty.values.filter(propertyVisibleForAllFeatures);
  }

  _showMessage (message) {
    this.prepend(h('.d-alert.d-alert-info.d-alert-small.empty-message', message));
  }

  _clearMessage () {
    this.querySelector('.empty-message')?.remove();
  }

  /**
   * Determine a common value for a given property.
   *
   * @param {string} property - name of the property
   * @return {string|number} - if all selected features have the same value, return that value, otherwise return null
   * @private
   */
  _determineCommonPropertyValue (property) {
    let commonValue = null;

    for (const feature of this._selectedFeatures) {
      const value = getItemPropertyValue(feature, property);
      if (commonValue === null) {
        commonValue = value;
      } else if (value !== commonValue) {
        commonValue = null;
        break;
      }
    }

    return commonValue;
  }

  _setPropertyVisible (property, visible) {
    this.querySelector(`[name=${property}]`)
      .closest('.feature-properties-form > div').hidden = !visible;
  }

  _setPropertyValue (property, value) {
    const input = this.querySelector(`[name=${property}]`);
    if (value === null) {
      this._makeInputBlank(input);
    } else {
      if (input.type === 'checkbox') {
        input.checked = value;
      } else if (input instanceof Select) {
        input.selection = value;
      } else if (property === FeatureProperty.OPACITY) {
        input.value = opacityAsFractionToTransparencyAsPercent(value);
      } else {
        input.value = value;
      }
    }
  }

  _makeInputBlank (input) {
    if (input.type === 'number') {
      input.required = false;
      input.value = '';
    } else if (input.type === 'checkbox') {
      input.indeterminate = true;
    } else if (input.tagName === 'SELECT') {
      input.selectedIndex = 0; // select the blank option
    } else if (input.tagName === 'D-SLIDER-NUMBER-INPUT') {
      input.makeUnset();
    } else if (input.tagName === 'D-COLOR-PICKER') {
      input.indeterminate = true;
    }
  }

  _onPropertyChange (e) {
    if (this._selectedFeatures.length === 0) {
      return;
    }

    const input = e.target;
    const delegate = e.delegateTarget;

    // Here we ignore some of the input/change events from input elements because there are some duplicates
    // For a list of what event is triggered by which input see the comment in : https://jira.disy.net/browse/CADENZA-8070
    if (input.nodeName === 'INPUT') {
      if (input.type === 'number' && e.type === 'change') {
        // Ignore 'change' events from number inputs
        // We could ignore the 'input' event instead, but this makes the change show immediately in the GUI without having to lose focus on the input
        return;
      } else if (input.type === 'checkbox' && e.type === 'input') {
        // ignore input events from checkboxes
        return;
      } else if (input.type === 'range' && e.type === 'input') {
        e.preventDefault(); // Prevents the immediate next change event from firing
      }
    }

    if (!input.validity || input.validity.valid) {
      const property = input.getAttribute('name') || delegate.getAttribute('name');
      const value = getInputValue(input);

      if (property === FeatureProperty.BACKGROUND_VISIBLE) {
        this._backgroundColorPicker.disabled = !value;
      }

      this._applyPropertyToSelectedFeatures(property, value);

      // In order to stop spamming the undo/redo history we save styles only on 'change' event, or 'input' event for number inputs.
      if (e.type === 'change' || input.type === 'number') {
        this._dispatchFeaturesChangeEvent();
      }
    }
  }

  _applyPropertyToSelectedFeatures (property, value) {
    this._selectedFeatures.forEach(feature => {
      applyPropertyValueToFeature(property, value, feature);
    });
  }

  _dispatchFeaturesChangeEvent () {
    this.dispatchEvent(new CustomEvent(
      SKETCH_EVENTS.FEATURE_CHANGE,
      { bubbles: true, detail: { features: Array.from(this._selectedFeatures) } }
    ));

    this._selectedFeatures.forEach(feature => {
      feature.set(SketchFeatureProperty.OLD_STYLE_ITEM, null);
      feature.set(SketchFeatureProperty.OLD_GEOMETRY, null);
    });
  }

  get _allPropertyInputs () {
    return this.querySelectorAll('.property-input');
  }

}

customElements.define(NAME, SketchFeaturePropertiesEditor);

function getItemPropertyValue (feature, prop) {
  const style = feature.get(SketchFeatureProperty.STYLE_ITEM).style;
  if (prop === FeatureProperty.OPACITY) {
    return getOpacity(style[FeatureProperty.FOREGROUND_COLOR]);
  }
  return convertPropertyValueFromModelToOlStyle(prop, style[prop]);
}

function convertPropertyValueFromModelToOlStyle (property, value) {
  if (property.endsWith('Color') && value != null) {
    return toHexColor(value);
  } else if (property === FeatureProperty.ROTATION && value == null) {
    return 0;
  } else if (property === FeatureProperty.FONT_SIZE && value != null) {
    return parseFloat(value);
  }
  return value;
}

const getVisibleProperties = sketchItemStrategy({
  [SketchObjectType.LINE]: getLineVisibleProperties,
  [SketchObjectType.OVAL]: getPolygonVisibleProperties,
  [SketchObjectType.POINT]: getPointVisibleProperties,
  [SketchObjectType.POLYGON]: getPolygonVisibleProperties,
  [SketchObjectType.POLYLINE]: getLineVisibleProperties,
  [SketchObjectType.RECTANGLE]: getPolygonVisibleProperties,
  [SketchObjectType.IMAGE]: () => {
    return [];
  },
  [SketchObjectType.TEXT]: () => {
    return [ FeatureProperty.FONT_FAMILY, FeatureProperty.FONT_SIZE,
      FeatureProperty.BOLD, FeatureProperty.ITALIC,
      FeatureProperty.FOREGROUND_COLOR, FeatureProperty.BACKGROUND_VISIBLE,
      FeatureProperty.BACKGROUND_COLOR, FeatureProperty.ANCHOR, FeatureProperty.ROTATION,
      FeatureProperty.OPACITY ];
  }
});

function getInputValue (input) {
  if (input.type === 'checkbox') {
    return input.checked;
  }
  if (input instanceof Select) {
    return input.selection[0].id;
  }
  return input.valueAsNumber || input.value;
}

function getLineVisibleProperties () {
  return [ FeatureProperty.FOREGROUND_COLOR, FeatureProperty.LINE_WIDTH, FeatureProperty.LINE_START_DECORATION, FeatureProperty.LINE_END_DECORATION ];
}

function getPolygonVisibleProperties ({ style }) {
  if (style[StyleProperty.FILL_SIGNATURE_TYPE] === FillSignatureType.IMAGED) {
    return [ FeatureProperty.BORDER_COLOR, FeatureProperty.LINE_WIDTH ];
  }
  return [ FeatureProperty.BORDER_COLOR, FeatureProperty.LINE_WIDTH,
    FeatureProperty.FOREGROUND_COLOR, FeatureProperty.OPACITY ];
}

// covers point, point icon
function getPointVisibleProperties ({ style }) {
  if (isRenderedAsPointImage(style)) {
    return [ FeatureProperty.ROTATION ];
  }
  return [ FeatureProperty.BORDER_COLOR, FeatureProperty.LINE_WIDTH,
    FeatureProperty.SIGNATURE_SIZE, FeatureProperty.FOREGROUND_COLOR, FeatureProperty.OPACITY ];
}

function createColorPicker (name) {
  const picker = new ColorPicker(DEFAULT_COLOR_PALETTE);
  picker.setAttribute('name', name);
  picker.classList.add('property-input', 'is-grow');
  return picker;
}

function createInput (classes, options = {}) {
  return h(`input${classes}`, options);
}

function createFontFamilySelect () {
  const fontFamilySelect = h(
    'select.property-input.form-control',
    { name: FeatureProperty.FONT_FAMILY },
    h('option', { hidden: true, disabled: true, value: '' }, '') // invisible, can only be selected programmatically (when no there's no common value)
  );

  fontFamilySelect.append(...Object.entries(fonts).map(([ name ]) => h('option', {
    value: name,
    textContent: name
  })));

  return fontFamilySelect;
}

function createAnchorSelect () {
  const anchorSelect = h(
    'select.property.property-input.form-control',
    { name: FeatureProperty.ANCHOR },
    h('option', { hidden: true, disabled: true, value: '' }, '') // invisible, can only be selected programmatically (when no there's no common value)
  );
  anchorSelect.append(...ANCHORS.map(anchor => h('option', {
    value: anchor,
    textContent: i18n(`style.anchor.${anchor}`)
  })));
  return anchorSelect;
}

function createLineDecorationSelect (name) {
  const data = [
    { id: LineDecoration.NONE, text: i18n('style.lineDecoration.none'), icon: icon(horizontalLineIcon) },
    { id: LineDecoration.ARROW, text: i18n('style.lineDecoration.arrow'), icon: icon(arrowIcon) },
    { id: LineDecoration.FILLED_ARROW, text: i18n('style.lineDecoration.filledArrow'), icon: icon(filledArrowIcon) },
    { id: LineDecoration.CROSSBAR, text: i18n('style.lineDecoration.crossbar'), icon: icon(crossBarIcon) }
  ];
  const select = new Select({
    data,
    styleClass: 'property-input'
  });
  select.setAttribute('name', name);

  return select;
}

function createSlider (name, options = {}) {
  const slider = new SliderNumberInput({
    min: options.min || 0,
    max: options.max || 100,
    value: options.value || 0,
    unit: options.unit || PERCENT_UNIT_SYMBOL,
    emitEventOnEveryChange: true
  });
  slider.classList.add(...[ 'property', 'property-input' ]);
  slider.setAttribute('name', name);
  return slider;
}

// feature style manipulation functions:

function applyPropertyValueToFeature (property, value, feature) {
  // save unchanged style for undo/redo
  if (!feature.get(SketchFeatureProperty.OLD_STYLE_ITEM)) {
    feature.set(SketchFeatureProperty.OLD_STYLE_ITEM, cloneDeep(feature.get(SketchFeatureProperty.STYLE_ITEM)));
  }

  if (property === FeatureProperty.OPACITY) {
    applyTransparencyToFeature(value, feature);
  } else if (isColorProperty(property, value)) {
    applyColorToFeature(property, value, feature);
  } else if (property === FeatureProperty.FONT_SIZE) {
    applyFontSizeToFeature(value, feature);
  } else {
    applyPropertyValueToFeatureDirectly(property, value, feature);
  }
}

/**
 * Apply the given transparency value to the given feature.
 * Opacity is not an independent property, but it modified either fillColor or backgroundColor.
 * Which property is modified depends on which are available in the given feature's style.
 *
 * @param {number} transparency - transparency a percentage (0 => completely opaque and 100 => completely transparent)
 * @param {Feature} feature - the target feature
 */
function applyTransparencyToFeature (transparency, feature) {
  PROPERTIES_WITH_TRANSPARENCY.forEach(property => {
    const olColor = getColorFromFeatureStyle(feature, property);
    if (olColor) {
      const newColor = toRgbaColor([
        olColor[0],
        olColor[1],
        olColor[2],
        transparencyAsPercentToOpacityAsFraction(transparency)
      ]);
      applyPropertyValueToFeatureDirectly(property, newColor, feature);
    }
  });
}

/**
 * Applies the given RGB color the given property of the given feature, while conserving the current alpha value.
 *
 * @param {string} property - name of the property to modify
 * @param {string} rgbColor - rgb color such as '#ffffff')
 * @param {Feature} feature - the target feature
 */
function applyColorToFeature (property, rgbColor, feature) {
  const currentAlpha = getColorFromFeatureStyle(feature, property)[3];
  const olColor = getColorWithOpacity(rgbColor, currentAlpha);
  applyPropertyValueToFeatureDirectly(property, olColor, feature);
}

function applyFontSizeToFeature (fontSize, feature) {
  applyPropertyValueToFeatureDirectly(FeatureProperty.FONT_SIZE, fontSize + 'pt', feature);
}

function applyPropertyValueToFeatureDirectly (property, value, feature) {
  const item = feature.get(SketchFeatureProperty.STYLE_ITEM);
  item.style[property] = value;

  const previousZIndex = getMainStyle(feature).getZIndex();

  const newStyle = modelStyleToOpenLayersStyle(item);
  feature.setStyle(newStyle);
  // Since the new style might be a function which returns a new Style object, we need to copy the zIndex manually
  getMainStyle(feature).setZIndex(previousZIndex);
  feature.changed();
}

/**
 * Extract a color value from the feature's style based on the specified property.
 *
 * @param {Feature} feature - the target feature
 * @param {string} property - name of the property
 * @return {{"'0'": number, "'3'": number, "'2'": number, "'1'": number}} - The color as an array of 4 numbers (rgba)
 */
function getColorFromFeatureStyle (feature, property) {
  const style = getMainStyle(feature);
  let color;
  // For line, we only have one color but it's actually called FOREGROUND_COLOR for better consistency with the backend
  if (property === FeatureProperty.BORDER_COLOR || isLineFeature(feature)) {
    color = style.getStroke()?.getColor() || style.getImage()?.getStroke()?.getColor();
  } else if (property === FeatureProperty.FOREGROUND_COLOR) {
    color = style.getFill()?.getColor() || style.getImage()?.getFill().getColor() || style.getText()?.getFill()?.getColor();
  } else if (property === FeatureProperty.BACKGROUND_COLOR) {
    color = style.getText()?.getBackgroundFill()?.getColor();
  } else {
    throw new Error(`Not a supported color property: ${property}`);
  }

  if (typeof color === 'string') {
    color = parseColor(color);
  }

  return color;
}

function isLineFeature (feature) {
  const type = feature.get(SketchFeatureProperty.STYLE_ITEM).type;
  return type === SketchObjectType.LINE || type === SketchObjectType.POLYLINE;
}

function isColorProperty (property, value) {
  return property.endsWith('Color') && value != null && value.startsWith('#');
}
